import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { API, createProxyRequest } from '../helpers';

// 增强的存储实现，处理各种边缘情况
const customStorage = {
  getItem: (name) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return null;
      }
      
      const item = localStorage.getItem(name);
      if (!item) {
        return null;
      }
      
      const parsed = JSON.parse(item);
      
      // 验证数据结构完整性
      if (parsed && typeof parsed === 'object') {
        // 确保 savedKeys 是数组
        if (parsed.state && !Array.isArray(parsed.state.savedKeys)) {
          parsed.state.savedKeys = [];
        }
        
        // 确保基本字段存在
        if (parsed.state) {
          parsed.state.apikey = parsed.state.apikey || '';
          parsed.state.selectedService = parsed.state.selectedService || { name: '', url: '' };
          parsed.state.savedApikey = parsed.state.savedApikey || '';
          parsed.state.savedService = parsed.state.savedService || { name: '', url: '' };
          parsed.state.currentKeyIndex = typeof parsed.state.currentKeyIndex === 'number' ? parsed.state.currentKeyIndex : 0;
        }
      }
      
      return parsed;
    } catch (error) {
      console.warn('Failed to parse stored data:', error);
      try {
        localStorage.removeItem(name);
      } catch (removeError) {
        console.warn('Failed to remove corrupted data:', removeError);
      }
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      
      // 验证数据结构
      if (value && value.state) {
        // 确保 savedKeys 是数组
        if (!Array.isArray(value.state.savedKeys)) {
          value.state.savedKeys = [];
        }
        
        // 限制 savedKeys 数量以避免存储过大
        if (value.state.savedKeys.length > 50) {
          value.state.savedKeys = value.state.savedKeys.slice(0, 50);
        }
      }
      
      localStorage.setItem(name, JSON.stringify(value));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, attempting cleanup...');
        try {
          // 尝试清理旧数据
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key !== name && key.startsWith('query-')) {
              localStorage.removeItem(key);
            }
          });
          
          // 重试保存
          localStorage.setItem(name, JSON.stringify(value));
        } catch (retryError) {
          console.error('Failed to save even after cleanup:', retryError);
        }
      } else {
        console.error('Failed to save to localStorage:', error);
      }
    }
  },
  removeItem: (name) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(name);
      }
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  }
};

// 会话标记键
const SESSION_KEY = 'query-session-id';

// 生成会话ID
const generateSessionId = () => Date.now() + '-' + Math.random().toString(36).substr(2, 9);

// 检查是否为新会话
const isNewSession = () => {
  const currentSessionId = sessionStorage.getItem(SESSION_KEY);
  if (!currentSessionId) {
    // 没有会话ID，说明是新会话
    const newSessionId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, newSessionId);
    return true;
  }
  return false;
};

// 查询状态store
export const useQueryStore = create(
  persist(
    immer((set, get) => ({
      // 状态 - 添加更安全的初始化
      apikey: '',
      selectedService: { name: '', url: '' },
      savedApikey: '', // 已验证并保存的Key (保持向后兼容)
      savedService: { name: '', url: '' }, // 已验证并保存的服务 (保持向后兼容)
      // 新增：多个保存的密钥 - 确保始终是数组
      savedKeys: [], // 格式: [{ id, apikey, service, alias, lastQueryTime, queryData }]
      currentKeyIndex: 0, // 当前选中的密钥索引
      queryData: null,
      isLoading: false,
      error: null,
      lastQueryTime: null, // 记录最后查询时间
      
      // Actions
      setAPIKey: (key) => set((state) => {
        state.apikey = key;
      }),
      
      setSelectedService: (service) => set((state) => {
        state.selectedService = service;
      }),
      
      setQueryData: (data) => set((state) => {
        state.queryData = data;
        state.lastQueryTime = Date.now();
      }),
      
      setLoading: (loading) => set((state) => {
        state.isLoading = loading;
      }),
      
      setError: (error) => set((state) => {
        state.error = error;
      }),

      // 新增：保存密钥到多密钥列表
      saveKeyToList: (apikey, service, alias = null) => set((state) => {
        // 确保 savedKeys 是数组
        if (!Array.isArray(state.savedKeys)) {
          state.savedKeys = [];
        }
        
        const keyId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        const newKey = {
          id: keyId,
          apikey,
          service,
          alias: alias || `已保存密钥`,
          lastQueryTime: Date.now(),
          queryData: state.queryData
        };
        
        // 检查是否已存在相同的key
        const existingIndex = state.savedKeys.findIndex(key => key?.apikey === apikey);
        if (existingIndex !== -1) {
          // 更新现有key
          state.savedKeys[existingIndex] = { 
            ...state.savedKeys[existingIndex], 
            ...newKey, 
            id: state.savedKeys[existingIndex].id 
          };
        } else {
          // 添加新key
          state.savedKeys.push(newKey);
        }
        
        // 设置当前选中为最新的key
        state.currentKeyIndex = existingIndex !== -1 ? existingIndex : state.savedKeys.length - 1;
        
        // 同时更新旧的单密钥字段以保持兼容性
        state.savedApikey = apikey;
        state.savedService = service;
      }),

      // 新增：删除指定密钥
      removeKeyFromList: (keyId) => set((state) => {
        // 确保 savedKeys 是数组
        if (!Array.isArray(state.savedKeys)) {
          state.savedKeys = [];
          return;
        }
        
        const index = state.savedKeys.findIndex(key => key?.id === keyId);
        if (index !== -1) {
          state.savedKeys = state.savedKeys.filter((_, i) => i !== index);
          
          // 调整当前索引
          if (state.currentKeyIndex >= state.savedKeys.length) {
            state.currentKeyIndex = Math.max(0, state.savedKeys.length - 1);
          }
          
          // 更新兼容性字段
          if (state.savedKeys.length > 0) {
            const currentKey = state.savedKeys[state.currentKeyIndex];
            if (currentKey) {
              state.savedApikey = currentKey.apikey || '';
              state.savedService = currentKey.service || { name: '', url: '' };
            }
          } else {
            state.savedApikey = '';
            state.savedService = { name: '', url: '' };
          }
        }
      }),

      // 新增：设置当前密钥索引
      setCurrentKeyIndex: (index) => set((state) => {
        // 确保 savedKeys 是数组
        if (!Array.isArray(state.savedKeys)) {
          state.savedKeys = [];
          state.currentKeyIndex = 0;
          return;
        }
        
        if (index >= 0 && index < state.savedKeys.length) {
          state.currentKeyIndex = index;
          const currentKey = state.savedKeys[index];
          if (currentKey) {
            state.savedApikey = currentKey.apikey || '';
            state.savedService = currentKey.service || { name: '', url: '' };
            state.queryData = currentKey.queryData || null;
          }
        }
      }),

      // 新增：获取当前选中的密钥
      getCurrentKey: () => {
        const { savedKeys, currentKeyIndex } = get();
        
        // 确保 savedKeys 是数组
        if (!Array.isArray(savedKeys) || savedKeys.length === 0) {
          return null;
        }
        
        const safeIndex = Math.max(0, Math.min(currentKeyIndex || 0, savedKeys.length - 1));
        return savedKeys[safeIndex] || null;
      },

      // 新增：更新密钥别名
      updateKeyAlias: (keyId, alias) => set((state) => {
        // 确保 savedKeys 是数组
        if (!Array.isArray(state.savedKeys)) {
          state.savedKeys = [];
          return;
        }
        
        const index = state.savedKeys.findIndex(key => key?.id === keyId);
        if (index !== -1) {
          state.savedKeys[index].alias = alias;
        }
      }),
      
      // 清除所有数据
      clearAll: () => set((state) => {
        state.apikey = '';
        state.selectedService = { name: '', url: '' };
        state.savedApikey = '';
        state.savedService = { name: '', url: '' };
        state.savedKeys = [];
        state.currentKeyIndex = 0;
        state.queryData = null;
        state.isLoading = false;
        state.error = null;
        state.lastQueryTime = null;
      }),
      
      // 执行查询
      performQuery: async () => {
        const { apikey, selectedService } = get();
        
        if (!apikey || !selectedService.url) {
          set((state) => {
            state.error = '缺少必要的查询参数';
          });
          return;
        }
        
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        
        let queryData = { balance: 0, usage: 0, logs: [], tokenValid: false };
        
        try {
          // 查询余额信息
          if ((process.env.REACT_APP_SHOW_BALANCE || "true") === "true") {
            const subscription = await createProxyRequest(
              selectedService.url, 
              '/v1/dashboard/billing/subscription',
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${apikey}` },
              }
            );
            const subscriptionData = subscription.data;
            queryData.balance = subscriptionData.hard_limit_usd;
            queryData.tokenValid = true;

            let now = new Date();
            let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
            let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
            let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
            const res = await createProxyRequest(
              selectedService.url,
              `/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${apikey}` },
              }
            );
            queryData.usage = res.data.total_usage / 100;
          }
        } catch (e) {
          set((state) => {
            state.isLoading = false;
            state.error = '令牌无效或已过期';
          });
          throw new Error('令牌无效或已过期');
        }

        try {
          // 查询日志详情
          if ((process.env.REACT_APP_SHOW_DETAIL || "true") === "true") {
            const logRes = await createProxyRequest(
              selectedService.url,
              `/api/log/token?key=${apikey}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${apikey}` },
              }
            );
            const { success, data: logData } = logRes.data;

            if (success) {
              // 保留完整的日志数据
              const reversedLogs = logData.reverse();
              queryData.logs = reversedLogs;
            }
          }
        } catch (e) {
          // 日志查询失败不影响余额显示
        }

        set((state) => {
          state.queryData = queryData;
          state.savedApikey = apikey; // 保存验证成功的Key
          state.savedService = selectedService; // 保存验证成功的服务
          state.isLoading = false;
          state.error = null;
          state.lastQueryTime = Date.now();
        });
        
        // 同时保存到多密钥列表
        get().saveKeyToList(apikey, selectedService);
        
        return queryData;
      },
      
      // 刷新数据
      refreshData: async () => {
        return get().performQuery();
      },
      
      // 使用保存的Key进行查询
      performSavedQuery: async () => {
        const { savedApikey, savedService } = get();
        
        if (!savedApikey || !savedService.url) {
          set((state) => {
            state.error = '没有保存的查询参数';
          });
          throw new Error('没有保存的查询参数');
        }
        
        // 直接使用保存的Key和服务进行查询，同时更新当前选择的服务
        set((state) => {
          state.isLoading = true;
          state.error = null;
          // 将保存的服务设置为当前选择的服务，这样Search页面就能正确显示
          state.selectedService = savedService;
          state.apikey = savedApikey; // 同时更新当前apikey
        });
        
        let queryData = { balance: 0, usage: 0, logs: [], tokenValid: false };
        
        try {
          // 查询余额信息
          if ((process.env.REACT_APP_SHOW_BALANCE || "true") === "true") {
            const subscription = await createProxyRequest(
              savedService.url, 
              '/v1/dashboard/billing/subscription',
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${savedApikey}` },
              }
            );
            const subscriptionData = subscription.data;
            queryData.balance = subscriptionData.hard_limit_usd;
            queryData.tokenValid = true;

            let now = new Date();
            let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
            let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
            let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
            const res = await createProxyRequest(
              savedService.url,
              `/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${savedApikey}` },
              }
            );
            queryData.usage = res.data.total_usage / 100;
          }
        } catch (e) {
          set((state) => {
            state.isLoading = false;
            state.error = '令牌无效或已过期';
          });
          throw new Error('令牌无效或已过期');
        }

        try {
          // 查询日志详情
          if ((process.env.REACT_APP_SHOW_DETAIL || "true") === "true") {
            const logRes = await createProxyRequest(
              savedService.url,
              `/api/log/token?key=${savedApikey}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${savedApikey}` },
              }
            );
            const { success, data: logData } = logRes.data;

            if (success) {
              // 保留完整的日志数据
              const reversedLogs = logData.reverse();
              queryData.logs = reversedLogs;
            }
          }
        } catch (e) {
          // 日志查询失败不影响余额显示
        }

        set((state) => {
          state.queryData = queryData;
          state.isLoading = false;
          state.error = null;
          state.lastQueryTime = Date.now();
        });
        
        // 更新当前选中密钥的查询数据
        const currentKey = get().getCurrentKey();
        if (currentKey) {
          set((state) => {
            const index = state.savedKeys.findIndex(key => key.id === currentKey.id);
            if (index !== -1) {
              state.savedKeys[index].queryData = queryData;
              state.savedKeys[index].lastQueryTime = Date.now();
            }
          });
        }
        
        return queryData;
      },

      // 新增：使用指定密钥进行查询
      performQueryWithKey: async (keyId) => {
        const { savedKeys } = get();
        
        // 确保 savedKeys 是数组
        if (!Array.isArray(savedKeys) || savedKeys.length === 0) {
          throw new Error('没有保存的密钥');
        }
        
        const keyIndex = savedKeys.findIndex(key => key?.id === keyId);
        
        if (keyIndex === -1) {
          throw new Error('密钥不存在');
        }
        
        const targetKey = savedKeys[keyIndex];
        
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.currentKeyIndex = keyIndex;
          state.selectedService = targetKey.service;
          state.apikey = targetKey.apikey;
          state.savedApikey = targetKey.apikey;
          state.savedService = targetKey.service;
        });
        
        let queryData = { balance: 0, usage: 0, logs: [], tokenValid: false };
        
        try {
          // 查询余额信息
          if ((process.env.REACT_APP_SHOW_BALANCE || "true") === "true") {
            const subscription = await createProxyRequest(
              targetKey.service.url, 
              '/v1/dashboard/billing/subscription',
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${targetKey.apikey}` },
              }
            );
            const subscriptionData = subscription.data;
            queryData.balance = subscriptionData.hard_limit_usd;
            queryData.tokenValid = true;

            let now = new Date();
            let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
            let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
            let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
            const res = await createProxyRequest(
              targetKey.service.url,
              `/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${targetKey.apikey}` },
              }
            );
            queryData.usage = res.data.total_usage / 100;
          }
        } catch (e) {
          set((state) => {
            state.isLoading = false;
            state.error = '令牌无效或已过期';
          });
          throw new Error('令牌无效或已过期');
        }

        try {
          // 查询日志详情
          if ((process.env.REACT_APP_SHOW_DETAIL || "true") === "true") {
            const logRes = await createProxyRequest(
              targetKey.service.url,
              `/api/log/token?key=${targetKey.apikey}`,
              {
                method: 'GET',
                headers: { Authorization: `Bearer ${targetKey.apikey}` },
              }
            );
            const { success, data: logData } = logRes.data;

            if (success) {
              // 保留完整的日志数据
              const reversedLogs = logData.reverse();
              queryData.logs = reversedLogs;
            }
          }
        } catch (e) {
          // 日志查询失败不影响余额显示
        }

        set((state) => {
          state.queryData = queryData;
          state.isLoading = false;
          state.error = null;
          state.lastQueryTime = Date.now();
          
          // 更新密钥的查询数据
          state.savedKeys[keyIndex].queryData = queryData;
          state.savedKeys[keyIndex].lastQueryTime = Date.now();
        });
        
        return queryData;
      },
      
      // 检查是否有有效的Key和服务
      hasValidKey: () => {
        const { apikey, selectedService } = get();
        return !!(apikey && selectedService.url);
      },
      
      // 检查是否有查询数据
      hasQueryData: () => {
        const { queryData } = get();
        return !!queryData;
      },
      
      // 检查是否有已保存的有效Key
      hasSavedKey: () => {
        const { savedApikey, savedService, savedKeys } = get();
        const safeKeys = Array.isArray(savedKeys) ? savedKeys : [];
        return !!(savedApikey && savedService.url) || safeKeys.length > 0;
      },

      // 新增：检查是否有多个保存的密钥
      hasMultipleSavedKeys: () => {
        const { savedKeys } = get();
        return Array.isArray(savedKeys) && savedKeys.length >= 2;
      },
      
      // 检查是否有完整的会话（Key + 数据）
      hasCompleteSession: () => {
        const { apikey, selectedService, queryData } = get();
        return !!(apikey && selectedService.url && queryData);
      },
      
      // 检查数据是否需要刷新（超过5分钟）
      needsRefresh: () => {
        const { lastQueryTime } = get();
        if (!lastQueryTime) return true;
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() - lastQueryTime > fiveMinutes;
      },
      
      // 检查是否应该自动查询（只在新会话时）
      shouldAutoQuery: () => {
        return isNewSession() && get().hasValidKey();
      },
      
      // 自动查询（用于新会话时）
      autoQuery: async () => {
        const { hasValidKey, hasQueryData, needsRefresh } = get();
        
        if (!hasValidKey()) {
          return false;
        }
        
        // 如果没有数据或需要刷新，则查询
        if (!hasQueryData() || needsRefresh()) {
          try {
            await get().performQuery();
            return true;
          } catch (error) {
            return false;
          }
        }
        
        return true;
      }
    })),
    {
      name: 'query-storage',
      storage: createJSONStorage(() => customStorage),
      partialize: (state) => ({
        // 持久化Key、服务和查询数据
        apikey: state.apikey,
        selectedService: state.selectedService,
        savedApikey: state.savedApikey,
        savedService: state.savedService,
        // 新增：持久化多密钥数据
        savedKeys: state.savedKeys.map(key => ({
          ...key,
          queryData: key.queryData ? {
            ...key.queryData,
            logs: key.queryData.logs ? key.queryData.logs.slice(0, 500) : []
          } : null
        })),
        currentKeyIndex: state.currentKeyIndex,
        queryData: state.queryData ? {
          ...state.queryData,
          // 只持久化最近500条日志以避免存储配额问题
          logs: state.queryData.logs ? state.queryData.logs.slice(0, 500) : []
        } : null,
        lastQueryTime: state.lastQueryTime,
      }),
      version: 4,
      migrate: (persistedState, version) => {
        // 添加更安全的错误处理
        try {
          if (version < 2) {
            return {
              apikey: persistedState?.apikey || '',
              selectedService: persistedState?.selectedService || { name: '', url: '' },
              savedApikey: persistedState?.apikey || '',
              savedService: persistedState?.selectedService || { name: '', url: '' },
              savedKeys: [],
              currentKeyIndex: 0,
              queryData: null,
              lastQueryTime: null,
            };
          }
          if (version < 3) {
            // 从v2迁移到v3，将单个保存的密钥转换为多密钥格式
            const savedKeys = [];
            if (persistedState?.savedApikey && persistedState?.savedService?.url) {
              savedKeys.push({
                id: Date.now() + '-migrated',
                apikey: persistedState.savedApikey,
                service: persistedState.savedService,
                alias: '已保存密钥',
                lastQueryTime: persistedState.lastQueryTime || Date.now(),
                queryData: persistedState.queryData
              });
            }
            
            return {
              ...persistedState,
              savedKeys: Array.isArray(savedKeys) ? savedKeys : [],
              currentKeyIndex: 0,
            };
          }
          
          if (version < 4) {
            // 从v3迁移到v4，增强数据完整性检查
            const state = { ...persistedState };
            
            // 确保 savedKeys 是数组
            if (!Array.isArray(state.savedKeys)) {
              state.savedKeys = [];
            }
            
            // 确保 currentKeyIndex 是有效数字
            if (typeof state.currentKeyIndex !== 'number' || state.currentKeyIndex < 0) {
              state.currentKeyIndex = 0;
            }
            
            // 确保 currentKeyIndex 不超出范围
            if (state.currentKeyIndex >= state.savedKeys.length) {
              state.currentKeyIndex = Math.max(0, state.savedKeys.length - 1);
            }
            
            return state;
          }
          
          // 确保 savedKeys 始终是数组
          if (persistedState && !Array.isArray(persistedState.savedKeys)) {
            persistedState.savedKeys = [];
          }
          
          return persistedState;
        } catch (error) {
          console.warn('Migration error, resetting to default state:', error);
          return {
            apikey: '',
            selectedService: { name: '', url: '' },
            savedApikey: '',
            savedService: { name: '', url: '' },
            savedKeys: [],
            currentKeyIndex: 0,
            queryData: null,
            lastQueryTime: null,
          };
        }
      },
    }
  )
); 
